import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1519143009590-e3800b9df468?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxzY2FmZm9sZGluZyUyMGNvbnN0cnVjdGlvbiUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzg0MzI3MjkzfDA&ixlib=rb-4.1.0&q=85";

const lines = ["ENGINEERING THE", "FRAMEWORK OF INDIA'S", "INFRASTRUCTURE."];

const lineVariant = {
  hidden: { y: 60, opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.9, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Industrial scaffolding framework"
          className="h-full w-full object-cover grayscale contrast-110"
        />
        <div className="absolute inset-0 bg-background/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/50" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-background/90 to-transparent" />
      </motion.div>

      {/* technical corner labels */}
      <motion.div
        style={{ opacity }}
        className="absolute top-28 right-6 md:right-12 lg:right-16 font-mono text-xs uppercase tracking-widest text-primary hidden sm:block"
      >
        [ 30 YEARS OF PRECISION ]
      </motion.div>

      <div className="relative z-10 flex h-full flex-col justify-end px-6 md:px-12 lg:px-16 pb-16 md:pb-20">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">
          Cuplock Scaffolding Specialists
        </div>
        <h1 className="font-bebas uppercase leading-[0.92] text-foreground text-[10.5vw] sm:text-[11vw] md:text-[11vw] lg:text-[9.5vw]">
          {lines.map((line, i) => (
            <span key={i} className="block">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-8 border-t border-border pt-8"
        >
          <p className="font-plex text-base md:text-lg text-muted-foreground max-w-md">
            Three decades manufacturing, renting and erecting cuplock scaffolding
            systems across India — with deep specialization in thermal power plants.
          </p>
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-muted-foreground">
            <ArrowDown size={16} className="animate-bounce text-primary" />
            Scroll to explore
          </div>
        </motion.div>
      </div>
    </section>
  );
};
